import React, { useState } from "react";
import { StyleSheet, View } from "react-native";
import { useRouter } from "expo-router";
import { useAuth } from "@/hooks/auth";
import { layout } from "@/constants";
import {
  ScreenLayout,
  Text,
  TextButton,
  Title,
  ScrollView,
  ConfirmDeleteDialog
} from "@/components";
import * as apiClient from "@/utils/serenity-api-client";
import { showToast } from "@/utils/toast";

/**
 * Account screen for managing account-level actions like deletion
 */
export default function AccountScreen() {
  const { signOut, getIdToken } = useAuth();
  const router = useRouter();
  const [deleteAccountVisible, setDeleteAccountVisible] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);

  const handleDeleteAccount = async () => {
    try {
      setIsDeletingAccount(true);
      const token = await getIdToken();
      if (!token) return;

      const result = await apiClient.deleteAccount(token);
      if (result.success) {
        await signOut();
        router.replace("/(drawer)/login");
      } else {
        showToast("Failed to delete account");
      }
    } catch {
      showToast("Failed to delete account");
    } finally {
      setIsDeletingAccount(false);
      setDeleteAccountVisible(false);
    }
  };

  return (
    <ScreenLayout>
      <ScrollView>
        <Title subtitle="Manage your account.">Account</Title>

        <View style={styles.deleteSection}>
          <Text size="small" color="secondary" style={styles.deleteText}>
            Permanently delete your account and all associated data including
            sessions, facts, and patterns. This action cannot be undone.
          </Text>
          <TextButton
            buttonText={isDeletingAccount ? "Deleting..." : "Delete my account"}
            variant="destructive"
            disabled={isDeletingAccount}
            onPress={() => setDeleteAccountVisible(true)}
            accessibilityLabel="Delete my account"
          />
        </View>

        <ConfirmDeleteDialog
          visible={deleteAccountVisible}
          title="Delete your account?"
          message="This will permanently delete your account, all sessions, facts, patterns, and sign you out. This cannot be undone."
          confirmText="DELETE"
          onConfirm={handleDeleteAccount}
          onCancel={() => setDeleteAccountVisible(false)}
        />
      </ScrollView>
    </ScreenLayout>
  );
}

const styles = StyleSheet.create({
  deleteSection: {
    marginTop: layout.space.xl,
    alignItems: "center",
    paddingBottom: layout.space.xl,
    gap: layout.space.sm
  },
  deleteText: {
    textAlign: "center",
    marginBottom: layout.space.sm
  }
});
